import React from 'react';
import Fetch from '../../common/util/_fetch';
import { ExportAssetType } from '../util/useAddPermissions';
import { Checkbox, Modal } from 'antd';
import {
  executeDownload,
  Flex,
  Grid,
  useAssetConfig,
  useAssetTranslation,
} from '@mss/mss-component-library';
import Button from '../../common/Button';
import moment from 'moment';

async function createDownloadLink(
  type: ExportAssetType,
  fields: string[],
  fieldLabels: Record<string, string>
) {
  try {
    const response = await Fetch.get(
      `/api/assets/csv_download/?type=${type}&fields=${fields.join(',')}&deleted=false`
    );

    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const text = await blob.text();
    const lines = text.split('\n');
    if (lines[0]) {
      lines[0] = fields.map(fieldName => fieldLabels[fieldName] || fieldName).join(',');
    }

    const url = window.URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
    executeDownload(url, `${type}-${moment().format('YYYY-MM-DD@HH.mm.ss')}.csv`);
    setTimeout(() => window.URL.revokeObjectURL(url), 100);
  } catch (err) {
    console.error(err);
  }
}

const defaultFields = {
  id: 'ID',
  operational_status: 'Operational Status',
  model_name: 'Model Name',
  type: 'Asset Type',
  custom_field_values: 'Custom Field Values',
};

type Props = {
  type: ExportAssetType;
  open: boolean;
  onCancel: () => void;
};

export function ExportAssetsModal({ open, onCancel, type }: Props) {
  const { config } = useAssetConfig();
  const { t } = useAssetTranslation();
  const fields = Object.values(config.assets)
    .map(asset => asset.form.configure.inputs)
    .flat()
    .reduce(
      (acc, input) => {
        return { ...acc, [input.fieldName]: input.label || input.fieldName };
      },
      {
        ...defaultFields,
        [config.listView.subTypeKey]: 'Asset subtype',
        ...(config.listView.assetType === 'field'
          ? { ['asset_data']: 'Raw field asset data' }
          : {}),
      }
    );

  const [selectedFields, setSelectedFields] = React.useState<string[]>(
    Object.keys(fields)
  );

  const handleFieldToggle = (fieldName: string) => {
    setSelectedFields(prev =>
      prev.includes(fieldName)
        ? prev.filter(f => f !== fieldName)
        : [...prev, fieldName]
    );
  };

  const handleSelectAll = () => {
    setSelectedFields(Object.keys(fields));
  };

  const handleDeselectAll = () => {
    setSelectedFields([]);
  };

  const allSelected = selectedFields.length === Object.keys(fields).length;
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      destroyOnClose
      width={600}
      maskClosable
      okButtonProps={{ disabled: selectedFields.length === 0 }}
      onOk={async () => {
        if (selectedFields.length === 0) return;
        await createDownloadLink(type, selectedFields, fields);
        onCancel();
      }}
      okText={t('Export to CSV')}
      cancelButtonProps={{ className: 'hidden' }}
      title={
        <Flex h="between" v="center" className="-mt-0.5">
          <div className="text-lg font-medium">
            {t('Select fields to export')}
          </div>
          <Button
            variant="primary"
            ghost={allSelected}
            onClick={allSelected ? handleDeselectAll : handleSelectAll}
            size="small"
            className="mr-6"
          >
            {t(allSelected ? 'Deselect All' : 'Select All')}
          </Button>
        </Flex>
      }
    >
      <Grid.Row>
        {Object.entries(fields).map(([fieldName, label]) => (
          <Grid.Col width={6} key={fieldName} className="mb-2">
            <Checkbox
              checked={selectedFields.includes(fieldName)}
              onChange={() => handleFieldToggle(fieldName)}
            >
              {t(label)}
            </Checkbox>
          </Grid.Col>
        ))}
      </Grid.Row>
    </Modal>
  );
}
